import { EnvironmentInjector, Injectable } from '@angular/core';
import { Auth0Client, createAuth0Client } from '@auth0/auth0-spa-js';
import {
  from,
  of,
  Observable,
  BehaviorSubject,
  combineLatest,
} from 'rxjs';
import { tap, concatMap, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { UserService } from '../shared/services/user/user.service';
import { StorageService } from '../shared/services/storage.service';
import { environment } from '../../environments/environment';
import { GoogleAnalyticsService } from '../shared/services/google-analytics.service';
import { PosthogService } from '../shared/services/posthog.service';
import { StorageType } from '../shared/models/constants';
import { AppInfoService } from '../shared/services/app-info.service';
import { NotificationService } from '../shared/services/notification.service';
import * as Sentry from '@sentry/angular-ivy';

@Injectable({
  providedIn: 'root',
})
export class Auth0Service {
  // Create an observable of Auth0 instance of client
  clientId = environment.auth0.clientId;
  appMetadata = environment.auth0.appMetadata;
  user: any;
  /** True when an admin is impersonating this session's user (from the
   * Auth0 token's app_metadata, same rule as the backend's is_impersonated). */
  isImpersonating = false;

  private auth0Options = {
    domain: environment.auth0.domain,
    clientId: environment.auth0.clientId,
    authorizationParams: {
      redirect_uri: `${window.location.origin}/callback`,
      audience: environment.auth0.apiIdentifier,
      scope: 'read:current_user',
    },
    // Persist the SDK's token cache so a reload is served from cache instead
    // of a silent-auth iframe. The iframe cannot work on a dev origin (see
    // below), and with the default in-memory cache every reload fell back to
    // it. Auth0 documents this as the mitigation for browsers that block
    // third-party cookies. No new exposure: loginToMedshorts() already writes
    // the raw id_token to localStorage under `auth0Token`.
    cacheLocation: 'localstorage' as const,
  };

  // createAuth0Client() runs a boot-time silent-auth iframe whenever it has no
  // usable cached token, and rejects on any error that is not login_required.
  // Auth0 will not skip the consent prompt for a client whose callback URL is
  // localhost — it cannot verify ownership of that host, so the client is never
  // "verifiable first-party" — and prompt=none therefore answers
  // consent_required rather than a code. Measured from http://localhost:4201:
  // {error: 'consent_required'} in 818ms.
  //
  // shareReplay(1) then cached that rejection for the whole page load, so
  // isAuthenticated$ errored, the route guard's navigation was cancelled, and
  // login() — which subscribes with only a next callback — silently never
  // called loginWithRedirect. The sign-in button's own routerLink="" bounced
  // to the homepage, which is what the failure looked like. On /callback it
  // was worse: a valid code sat unexchanged in the URL because the client was
  // never built, leaving the page wedged with no navigation at all.
  //
  // Build the client directly on failure. `new Auth0Client()` performs no boot
  // silent auth, so it always yields a usable client and the interactive login
  // stays reachable — regardless of why the iframe failed.
  auth0Client$ = from(
    createAuth0Client(this.auth0Options).catch(err => {
      Sentry.captureException(err, { level: 'warning' });
      this.deleteIsAuthedCookie();
      return new Auth0Client(this.auth0Options);
    }),
  ).pipe(
    shareReplay(1), // Every subscription receives the same shared value
  ) as Observable<Auth0Client>;

  // Define observables for SDK methods that return promises by default
  // For each Auth0 SDK method, first ensure the client instance is ready
  // concatMap: Using the client instance, call SDK method; SDK returns a promise
  // from: Convert that resulting promise into an observable
  isAuthenticated$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.isAuthenticated())),
    tap(res => (this.loggedIn = res)),
  );

  handleRedirectCallback$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.handleRedirectCallback())),
  );

  // Create subject and public observable of user profile data
  private userProfileSubject$ = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject$.asObservable();
  // Create a local property for login status
  loggedIn: any;
  private loggedInSubject$ = new BehaviorSubject<any>(null);

  constructor(
    private router: Router,
    private userService: UserService,
    private appInfo: AppInfoService,
    private storageService: StorageService,
    private http: HttpClient,
    private googleAnalticsService: GoogleAnalyticsService,
    private posthogService: PosthogService,
    private notificationService: NotificationService,
  ) {}

  getUser$(): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap(user => this.userProfileSubject$.next(user)),
    );
  }

  public userSubject(): BehaviorSubject<any> {
    return this.loggedInSubject$;
  }

  public get currentUserValue(): any {
    return this.userProfileSubject$.value;
  }

  localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams

    // On /callback a login is already in flight and handleAuthCallback() owns
    // establishing identity — it runs strictly AFTER the code exchange, so it
    // sees the new token. This path must not race it.
    //
    // It used to be impossible for it to: with the SDK's default in-memory
    // cache, isAuthenticated() was always false on the fresh /callback document,
    // so the branch below never fired there. `cacheLocation: 'localstorage'`
    // changed that — the cache now survives the redirect, isAuthenticated()
    // reads the PREVIOUS identity's token (getUser() consults the cache, never
    // the is.authenticated cookie that impersonate() deletes), and this
    // emits a second, earlier login. Downstream, checkAuth0UserData()'s single
    // `browserRefresh` boolean then swallows the emission that would have
    // re-run getMe() and re-routed — so impersonation kept the impersonator's
    // name and landing page while the session underneath was correctly swapped.
    if (window.location.pathname.startsWith('/callback')) {
      return;
    }

    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          return this.getUser$();
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      }),
    );
    checkAuth$.subscribe((response: { [key: string]: any } | boolean) => {
      // If authenticated, response will be user object
      // If not authenticated, response will be 'false'
      this.loggedIn = !!response;
      if (this.loggedIn) {
        // This is a page refresh
        this.loggedInSubject$.next(response[this.appMetadata].id);

        // Ensure backend usage session is active and PostHog context is set
        this.loginToMedshorts(response[this.appMetadata].id);
      }
    });
  }

  async login(redirectPath: string = '/') {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists

    //Set redirect target for app.components.ts
    const destination =
      redirectPath !== '/' && redirectPath !== undefined
        ? `?destination=${redirectPath}`
        : '';
    // try {
    //   await this.logout();
    // }
    // catch {}
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}/callback${destination}`,
        },
        appState: { target: redirectPath },
      });
    });
  }

  // The /callback route sometimes navigates twice with the same code+state.
  // The SDK consumes the state transaction on first use, so a second
  // handleRedirectCallback() throws "Invalid state" — and its error path
  // then tears down the first run's in-flight token exchange, denying the
  // login. A login redirect always starts from a fresh document, so at most
  // one legitimate callback processing exists per page load: make the
  // duplicate invocation a no-op and let the first run own the flow.
  private hasProcessedAuthCallback = false;

  handleAuthCallback() {
    // this.clearStoragesAndSetTheme();
    // Only the callback component should call this method
    // Call when app reloads after user logs in with Auth0
    if (this.hasProcessedAuthCallback) {
      return;
    }
    this.hasProcessedAuthCallback = true;
    let targetRoute: string; // Path to redirect to after login processsed
    const authComplete$ = this.handleRedirectCallback$.pipe(
      // Have client, now call method to handle auth callback redirect
      tap((cbRes: any) => {
        // Get and set target redirect route from callback results
        targetRoute =
          cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/';
      }),
      concatMap(() => {
        // Redirect callback complete; get user and login status
        return combineLatest([this.getUser$(), this.isAuthenticated$]);
      }),
    );
    // Subscribe to authentication completion observable
    // Response will be an array of user and login status
    authComplete$.subscribe({
      next: ([user, loggedIn]) => {
        this.router.navigate(['/loading']);
        const id = user[this.appMetadata].id;
        this.loginToMedshorts(id);
      },
      error: err => {
        // "Invalid state": the /callback URL was processed a second time after
        // the auth transaction was already consumed. If the first pass logged
        // us in, just leave the callback page; otherwise the login genuinely
        // failed — never route into /login here (a live Auth0 SSO session
        // would restart the redirect and loop forever).
        Sentry.captureException(err, { level: 'warning' });
        // isAuthenticated$ shares auth0Client$, so whatever failed the callback
        // can fail this too. With no error handler the subscribe died silently
        // and neither the notice nor the navigation ran, stranding the user on
        // /callback?code=... — treat an errored check as "not logged in".
        const leaveCallback = (loggedIn: boolean) => {
          if (!loggedIn) {
            this.notificationService.showNotify(
              'Sign-in could not be completed. Please try again.',
              'error',
              8000,
            );
          }
          this.router.navigate(['/']);
        };
        this.isAuthenticated$.subscribe({
          next: leaveCallback,
          error: () => leaveCallback(false),
        });
      },
    });
  }

  async loginToMedshorts(id: number) {
    try {
      const auth0Token = await this.getIdTokenClaims$().toPromise();
      this.storageService.set(StorageType.auth0Token, auth0Token.__raw);

      // Check for impersonation
      const appMetadata = auth0Token[this.appMetadata] || {};
      const impersonatorId = appMetadata.impersonator_id;
      this.isImpersonating = impersonatorId != null && impersonatorId !== id;

      if (impersonatorId) {
        console.log('Impersonation session - disabling analytics');
        this.posthogService.optOut();
      }

      try {
        let msClientResult = await this.userService.getMsClient$().toPromise();
        this.storageService.set(
          'msClient',
          JSON.stringify(msClientResult.data),
        );
        this.appInfo.setDomainConfigs();
      } catch {
        // All is good
      }

      this.user = await this.userService.login(id);
      this.loggedInSubject$.next(id);

      if (!impersonatorId) {
        // Track login in Google Analytics
        this.googleAnalticsService.eventEmitter(
          this.user.email,
          `Logged In ${this.user.role}`,
        );

        // Set PostHog user identity
        this.posthogService.IdentifyPosthogUser();
      }

    } catch (e) {
      console.error(e);
      this.googleAnalticsService.eventEmitter(this.user?.email, `Login Failure`);
      // ErrorInterceptor rethrows the body's `error` as a plain string.
      await this.failLogin(typeof e === 'string' ? e : e?.error?.error);
    }
  }

  /**
   * Failed login (backend rejection or missing token): end the session,
   * persist the reason across the logout redirect. Never restart the login
   * flow here — a warm SSO session loops it forever (the MDG-37 bounce).
   */
  private async failLogin(reason?: string) {
    try {
      await this.logoutFromMedshorts().toPromise();
    } catch {
      // Best effort — the backend session may already be gone.
    }
    this.posthogService.reset();
    this.clearStoragesAndSetTheme();
    sessionStorage.setItem(
      StorageType.loginFailureMessage,
      reason || 'Sign-in could not be completed. Please try again.',
    );
    // The Auth0 logout is a full-page navigation and MUST be the last thing
    // that happens — any navigation issued after it aborts the redirect,
    // leaving the SSO session alive (the old loop driver).
    await this.logoutAuth0();
  }

  async logout() {
    // Kill session on backend
    await this.logoutFromMedshorts().toPromise();

    // Reset PostHog user identity
    this.posthogService.reset();

    // Clear storage BEFORE the Auth0 logout: logoutAuth0() ends in
    // window.location.assign(), so anything sequenced after it races the
    // navigation and can be dropped. Leaving `auth0Token` behind is what makes
    // an expired-token logout retrigger on the next boot, in a loop.
    this.clearStoragesAndSetTheme();
    await this.logoutAuth0();
  }

  logoutAsync() {
    this.logoutFromMedshorts().toPromise();
    this.clearStoragesAndSetTheme();
    this.logoutAuth0();
  }

  logoutAuth0() {
    // Default behavior (unchanged): go back to current origin
    let logout_redirect = window.location.origin;

    // Existing override (keep it)
    if (environment.supplierConfig && environment.supplierConfig['logout_redirect']) {
      logout_redirect = environment.supplierConfig['logout_redirect'];
    }

    // NEW: highest priority override
    if (environment.customLogoutURL) {
      logout_redirect = environment.customLogoutURL;
    }

    // Ensure Auth0 client instance exists
    return new Promise((resolve, reject) => {
      return this.auth0Client$.subscribe((client: Auth0Client) => {
        resolve(
          client.logout({
            clientId: environment.auth0.clientId,
            logoutParams: { returnTo: logout_redirect },
          }),
        );
      });
    });
  }

  getTokenSilently$(options?): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) =>
        from(client.getTokenSilently(options)),
      ),
    );
  }

  getIdTokenClaims$(): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getIdTokenClaims())),
    );
  }

  public logoutFromMedshorts() {
    // Server request for terminating the session
    return this.http.get(`${environment.api_url_v1}/services/user/auth/logout`);
  }

  public clearStoragesAndSetTheme() {
    // save theme selection
    const configs = this.appInfo.configs;
    // Remove all from local and session storage
    localStorage.clear();
    sessionStorage.clear();
    // restore theme value
    this.appInfo.configs = configs;
  }

  // The SDK's session marker cookie. v2 namespaces it per client id, so the
  // name is derived rather than literal — read it from here, never inline it.
  private get isAuthedCookieName(): string {
    return `auth0.${this.clientId}.is.authenticated`;
  }

  public hasIsAuthedCookie(): boolean {
    return document.cookie
      .split(';')
      .some(c => c.trim().startsWith(`${this.isAuthedCookieName}=`));
  }

  public deleteIsAuthedCookie() {
    document.cookie = `${this.isAuthedCookieName}=false; expires=Thu, 01 Jan 1970 00:00:01 GMT;path=/`;
  }

  public updateUserSubject(user) {
    this.userProfileSubject$.next(user);
  }
}
